/**
 * AccordionSection — collapsible wrapper for the OS layout. Each section
 * (KPI stack, 10-Step Flow, 20-Capability Roadmap) uses one so the operator
 * can fold zones they aren't focused on. Open/closed state is persisted in
 * localStorage per section id so it survives reloads.
 */
import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";

const STORAGE_PREFIX = "nds_acc_open_";

export default function AccordionSection({
  id,
  kicker,
  title,
  defaultOpen = true,
  actions,
  children,
}) {
  const key = STORAGE_PREFIX + id;
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return defaultOpen;
    const raw = localStorage.getItem(key);
    if (raw === null) return defaultOpen;
    return raw === "1";
  });

  useEffect(() => {
    try { localStorage.setItem(key, open ? "1" : "0"); } catch { /* silent */ }
  }, [key, open]);

  return (
    <section className="nds-accordion" data-testid={`nds-accordion-${id}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`nds-accordion-toggle-${id}`}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 border-b border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors group"
      >
        <div className="flex items-center gap-3 min-w-0">
          <ChevronDown
            className={`w-4 h-4 text-nex-gold transition-transform duration-200 ${open ? "rotate-0" : "-rotate-90"}`}
          />
          <div className="text-left min-w-0">
            {kicker && (
              <div className="font-mono text-[0.55rem] tracking-[0.24em] text-nex-gold uppercase leading-tight">
                {kicker}
              </div>
            )}
            <div className="text-nex-silver font-serif text-lg md:text-xl font-semibold leading-tight truncate">
              {title}
            </div>
          </div>
        </div>
        {actions && (
          <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2 shrink-0">
            {actions}
          </div>
        )}
      </button>
      <div
        className={`overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${open ? "opacity-100" : "opacity-0 max-h-0"}`}
        style={open ? { maxHeight: "none" } : undefined}
        data-testid={`nds-accordion-body-${id}`}
      >
        {open && <div className="p-4 md:p-5">{children}</div>}
      </div>
    </section>
  );
}
